import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Container } from './Container'
import { LanguageSwitcher } from '../ui/LanguageSwitcher'
import { Button } from '../ui/Button'
import type { NavItem } from '../../types'

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  navItems: NavItem[]
  ctaLabel: string
  ctaHref: string
}

function isRouteHref(href?: string): boolean {
  return Boolean(href && href.startsWith('/') && !href.startsWith('/#'))
}

function NavLink({ href, label, onClick }: { href?: string; label: string; onClick: () => void }) {
  const classes = 'text-lg text-text-secondary hover:text-accent-light transition-colors'

  if (isRouteHref(href)) {
    return (
      <Link to={href as string} onClick={onClick} className={classes}>
        {label}
      </Link>
    )
  }

  return (
    <a href={href} onClick={onClick} className={classes}>
      {label}
    </a>
  )
}

export function MobileMenu({ isOpen, onClose, navItems, ctaLabel, ctaHref }: MobileMenuProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-bg/95 backdrop-blur-xl lg:hidden"
        >
          <Container className="flex h-full flex-col py-6">
            <div className="flex items-center justify-between">
              <span className="text-xl font-semibold text-gradient">EmpleaHoy IA</span>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-text-secondary hover:bg-surface-elevated"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <nav className="mt-12 flex flex-col gap-6">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.href ? (
                    <NavLink href={item.href} label={item.label} onClick={onClose} />
                  ) : (
                    <span className="text-2xl font-medium text-text-primary">{item.label}</span>
                  )}
                  {item.children && (
                    <div className="mt-3 ml-4 flex flex-col gap-3 border-l border-border-subtle pl-4">
                      {item.children.map((child) => (
                        <NavLink key={child.label} href={child.href} label={child.label} onClick={onClose} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="mt-auto flex flex-col gap-4">
              <LanguageSwitcher className="self-start" />
              <Button asChild size="lg" className="w-full">
                <a href={ctaHref} onClick={onClose}>{ctaLabel}</a>
              </Button>
            </div>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
